/**
 * Device identity + fingerprint collector (P2).
 *
 * Produces the stable hashes the GS engine keys on:
 *   - true_device_id : long-lived, triple-stored id (see persistence/store.ts)
 *   - device_hash    : composite of stable hardware/render fingerprints
 *   - browser_hash   : composite of browser-volatile attributes
 *   - cookie_hash    : hash bound to the persistent id (cookie/storage layer)
 * plus the individual canvas/webgl/audio/font sub-hashes for granular scoring.
 *
 * Every probe is wrapped so a single failure (private mode, blocked API,
 * locked-down browser) degrades to `null` instead of throwing.
 */

import type { DeviceSignals } from "../types.ts";
import { sha256Short } from "../util/sha256.ts";
import { safe, tryCatch } from "../util/async.ts";
import { resolveTrueDeviceId } from "../persistence/store.ts";

/* ---------------------------- canvas ---------------------------- */
function canvasFingerprint(): string | null {
  return tryCatch(() => {
    const canvas = document.createElement("canvas");
    canvas.width = 280;
    canvas.height = 60;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("GS\u{1F512} fp.io 0123", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("GS\u{1F512} fp.io 0123", 4, 17);
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = "rgb(255,0,255)";
    ctx.beginPath();
    ctx.arc(50, 30, 20, 0, Math.PI * 2, true);
    ctx.fill();
    return canvas.toDataURL();
  }, null);
}

/* ---------------------------- webgl ---------------------------- */
interface WebglParts {
  imageHash: string | null;
  paramsHash: string | null;
  vendor: string | null;
  renderer: string | null;
}

function webglParts(version: "webgl" | "webgl2"): WebglParts {
  return tryCatch<WebglParts>(() => {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext(version) as WebGLRenderingContext | null;
    if (!gl) return { imageHash: null, paramsHash: null, vendor: null, renderer: null };

    // Renderer + vendor + a curated set of stable params.
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    const params: string[] = [];
    const push = (label: string, v: unknown) => params.push(`${label}=${String(v)}`);
    let vendorStr: string | null = null;
    let rendererStr: string | null = null;
    if (dbg) {
      vendorStr = String(gl.getParameter((dbg as { UNMASKED_VENDOR_WEBGL: number }).UNMASKED_VENDOR_WEBGL) ?? "") || null;
      rendererStr = String(gl.getParameter((dbg as { UNMASKED_RENDERER_WEBGL: number }).UNMASKED_RENDERER_WEBGL) ?? "") || null;
      push("vendor", vendorStr);
      push("renderer", rendererStr);
    }
    const numeric = [
      gl.MAX_TEXTURE_SIZE, gl.MAX_RENDERBUFFER_SIZE, gl.MAX_VERTEX_ATTRIBS,
      gl.MAX_VARYING_VECTORS, gl.MAX_VERTEX_UNIFORM_VECTORS,
      gl.MAX_FRAGMENT_UNIFORM_VECTORS, gl.MAX_TEXTURE_IMAGE_UNITS,
      gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS, gl.ALIASED_LINE_WIDTH_RANGE,
      gl.ALIASED_POINT_SIZE_RANGE, gl.MAX_VIEWPORT_DIMS,
    ];
    for (const p of numeric) push(String(p), gl.getParameter(p));
    const exts = gl.getSupportedExtensions();
    if (exts) push("exts", exts.slice().sort().join(","));

    // Render a small scene and read pixels for an image hash.
    let imageData: string | null = null;
    try {
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      const verts = new Float32Array([-0.2, -0.9, 0, 0.4, -0.26, 0, 0, 0.7, 0]);
      gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
      const pixels = new Uint8Array(canvas.width * canvas.height * 4);
      gl.readPixels(0, 0, canvas.width, canvas.height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
      imageData = Array.from(pixels.slice(0, 256)).join(",");
    } catch {
      imageData = null;
    }

    return {
      imageHash: imageData,
      paramsHash: params.join("|") || null,
      vendor: vendorStr,
      renderer: rendererStr,
    };
  }, { imageHash: null, paramsHash: null, vendor: null, renderer: null });
}

/* ---------------------- plugins / mime / math / system colors ---------------------- */
function collectPlugins(): string[] | null {
  return tryCatch<string[] | null>(() => {
    const list = navigator.plugins;
    if (!list || list.length === 0) return [];
    const names: string[] = [];
    for (let i = 0; i < list.length; i++) {
      const name = list[i]?.name;
      if (name) names.push(name);
    }
    return names;
  }, null);
}

function mimeTypesSeed(): string | null {
  return tryCatch<string | null>(() => {
    const mt = navigator.mimeTypes;
    if (!mt || mt.length === 0) return "";
    const types: string[] = [];
    for (let i = 0; i < mt.length; i++) {
      const t = mt[i]?.type;
      if (t) types.push(t);
    }
    return types.sort().join(",");
  }, null);
}

/** Deterministic Math-implementation fingerprint (varies subtly across engines). */
function mathSeed(): string | null {
  return tryCatch<string | null>(() => {
    const ops: number[] = [
      Math.acos(0.123456789), Math.acosh(1.0000001), Math.asin(0.123456789),
      Math.asinh(0.123456789), Math.atan(0.123456789), Math.atanh(0.123456789),
      Math.cbrt(100), Math.cos(1e10), Math.cosh(10), Math.exp(10),
      Math.expm1(1), Math.log1p(10), Math.sin(1e10), Math.sinh(10),
      Math.tan(1e10), Math.tanh(0.123456789), Math.pow(Math.PI, -100),
    ];
    return ops.map((n) => n.toString()).join(",");
  }, null);
}

/** CSS system-color resolution fingerprint (theme/OS dependent). */
function systemColorsSeed(): string | null {
  return tryCatch<string | null>(() => {
    const keywords = [
      "ActiveText", "ButtonBorder", "ButtonFace", "ButtonText", "Canvas",
      "CanvasText", "Field", "FieldText", "GrayText", "Highlight",
      "HighlightText", "LinkText", "Mark", "MarkText", "VisitedText",
    ];
    const el = document.createElement("div");
    el.style.position = "absolute";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    const out: string[] = [];
    for (const kw of keywords) {
      el.style.color = kw;
      out.push(`${kw}=${getComputedStyle(el).color}`);
    }
    document.body.removeChild(el);
    return out.join("|");
  }, null);
}

/* ---------------------- UA Client Hints / media / locale ---------------------- */
async function collectUserAgentData(): Promise<DeviceSignals["user_agent_data"]> {
  try {
    const uaData = (navigator as unknown as {
      userAgentData?: {
        mobile?: boolean;
        platform?: string;
        getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>;
      };
    }).userAgentData;
    if (!uaData) return null;
    let high: Record<string, unknown> = {};
    if (typeof uaData.getHighEntropyValues === "function") {
      high = await uaData.getHighEntropyValues([
        "architecture", "bitness", "model", "platformVersion", "uaFullVersion",
      ]);
    }
    const str = (v: unknown): string | null => (typeof v === "string" && v.length > 0 ? v : null);
    return {
      platform: str(high.platform) ?? str(uaData.platform),
      platform_version: str(high.platformVersion),
      architecture: str(high.architecture),
      bitness: str(high.bitness),
      mobile: typeof uaData.mobile === "boolean" ? uaData.mobile : null,
      model: str(high.model),
      ua_full_version: str(high.uaFullVersion),
    };
  } catch {
    return null;
  }
}

async function collectMediaDevices(): Promise<DeviceSignals["media_devices"]> {
  try {
    const md = navigator.mediaDevices;
    if (!md || typeof md.enumerateDevices !== "function") return null;
    const devices = await md.enumerateDevices();
    const counts = { audio_input: 0, audio_output: 0, video_input: 0 };
    for (const d of devices) {
      if (d.kind === "audioinput") counts.audio_input++;
      else if (d.kind === "audiooutput") counts.audio_output++;
      else if (d.kind === "videoinput") counts.video_input++;
    }
    return counts;
  } catch {
    return null;
  }
}

/* ---------------------------- audio ---------------------------- */
function audioFingerprint(): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      const Ctx = (window as unknown as { OfflineAudioContext?: typeof OfflineAudioContext; webkitOfflineAudioContext?: typeof OfflineAudioContext })
        .OfflineAudioContext ||
        (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
      if (!Ctx) return resolve(null);
      const ctx = new Ctx(1, 5000, 44100);
      const osc = ctx.createOscillator();
      osc.type = "triangle";
      osc.frequency.value = 10000;
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -50;
      comp.knee.value = 40;
      comp.ratio.value = 12;
      comp.attack.value = 0;
      comp.release.value = 0.25;
      osc.connect(comp);
      comp.connect(ctx.destination);
      osc.start(0);
      ctx.startRendering();
      const timer = setTimeout(() => resolve(null), 800);
      ctx.oncomplete = (e) => {
        clearTimeout(timer);
        try {
          const data = e.renderedBuffer.getChannelData(0).slice(4500, 4600);
          let sum = 0;
          for (let i = 0; i < data.length; i++) sum += Math.abs(data[i]);
          resolve(sum.toString());
        } catch {
          resolve(null);
        }
      };
    } catch {
      resolve(null);
    }
  });
}

/* ---------------------------- fonts ---------------------------- */
const FONT_PROBE = [
  "Arial", "Arial Black", "Arial Narrow", "Calibri", "Cambria", "Comic Sans MS",
  "Consolas", "Courier New", "Georgia", "Helvetica", "Impact", "Lucida Console",
  "Palatino Linotype", "Segoe UI", "Tahoma", "Times New Roman", "Trebuchet MS",
  "Verdana", "Menlo", "Monaco", "Roboto", "Ubuntu", "Cantarell", "Noto Sans",
];

function detectFonts(): string[] | null {
  return tryCatch<string[] | null>(() => {
    const base = ["monospace", "sans-serif", "serif"];
    const testString = "mmmmmmmmmmlli";
    const testSize = "72px";
    const span = document.createElement("span");
    span.style.position = "absolute";
    span.style.left = "-9999px";
    span.style.fontSize = testSize;
    span.textContent = testString;
    document.body.appendChild(span);

    const baseline: Record<string, { w: number; h: number }> = {};
    for (const b of base) {
      span.style.fontFamily = b;
      baseline[b] = { w: span.offsetWidth, h: span.offsetHeight };
    }

    const available: string[] = [];
    for (const font of FONT_PROBE) {
      let detected = false;
      for (const b of base) {
        span.style.fontFamily = `'${font}',${b}`;
        if (span.offsetWidth !== baseline[b].w || span.offsetHeight !== baseline[b].h) {
          detected = true;
          break;
        }
      }
      if (detected) available.push(font);
    }
    document.body.removeChild(span);
    return available;
  }, null);
}

/* ---------------------------- screen ---------------------------- */
function collectScreen(): DeviceSignals["screen"] {
  return tryCatch<DeviceSignals["screen"]>(() => {
    const orientation = (screen as unknown as {
      orientation?: { type?: string; angle?: number };
    }).orientation;
    return {
      width: screen.width ?? null,
      height: screen.height ?? null,
      color_depth: screen.colorDepth ?? null,
      pixel_ratio: window.devicePixelRatio ?? null,
      refresh_rate: null, // populated by tamper.ts (rAF sampling) when available
      is_extended: (screen as unknown as { isExtended?: boolean }).isExtended ?? null,
      available_width: screen.availWidth ?? null,
      available_height: screen.availHeight ?? null,
      pixel_depth: screen.pixelDepth ?? null,
      orientation_type: orientation?.type ?? null,
      orientation_angle: typeof orientation?.angle === "number" ? orientation.angle : null,
    };
  }, {
    width: null, height: null, color_depth: null, pixel_ratio: null,
    refresh_rate: null, is_extended: null,
    available_width: null, available_height: null, pixel_depth: null,
    orientation_type: null, orientation_angle: null,
  });
}

/* ---------------------------- orchestrator ---------------------------- */
export async function collectDevice(persist: boolean): Promise<DeviceSignals> {
  const nav = navigator;

  const [canvas, audio] = await Promise.all([
    Promise.resolve(canvasFingerprint()),
    safe(audioFingerprint(), null, 1000),
  ]);
  const wgl1 = webglParts("webgl");
  const wgl2 = webglParts("webgl2");
  const fonts = detectFonts();

  const screen_ = collectScreen();

  // Enriched safe-subset seeds + async probes (all fail open to null).
  const pluginList = collectPlugins();
  const mimeSeed = mimeTypesSeed();
  const mathS = mathSeed();
  const sysColorsSeed = systemColorsSeed();
  const locale = tryCatch(() => Intl.DateTimeFormat().resolvedOptions().locale || null, null);
  const [userAgentData, mediaDevices] = await Promise.all([
    safe(collectUserAgentData(), null, 800),
    safe(collectMediaDevices(), null, 800),
  ]);

  const platform = tryCatch(() => nav.platform || null, null);
  // Page context (drives device.window_location / device.window_hostname rules).
  const windowLocation = tryCatch(() => window.location?.href || null, null);
  const windowHostname = tryCatch(() => window.location?.hostname || null, null);
  const vendor = tryCatch(() => (nav as unknown as { vendor?: string }).vendor || null, null);
  const timezone = tryCatch(() => Intl.DateTimeFormat().resolvedOptions().timeZone || null, null);
  const tzOffset = tryCatch(() => new Date().getTimezoneOffset(), null);
  const languages = tryCatch(() => (nav.languages ? Array.from(nav.languages) : nav.language ? [nav.language] : null), null);
  const deviceMemory = tryCatch(() => (nav as unknown as { deviceMemory?: number }).deviceMemory ?? null, null);
  const concurrency = tryCatch(() => nav.hardwareConcurrency ?? null, null);
  const touch = tryCatch(() => "ontouchstart" in window || nav.maxTouchPoints > 0, null);
  const dnt = tryCatch(() => {
    const v = (nav as unknown as { doNotTrack?: string }).doNotTrack ??
      (window as unknown as { doNotTrack?: string }).doNotTrack;
    return v === "1" || v === "yes" ? true : v === "0" || v === "no" ? false : null;
  }, null);
  const gpc = tryCatch(() => (nav as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl ?? null, null);

  // Persistent id (triple-store).
  const { id: trueDeviceId, present_before } = await resolveTrueDeviceId(persist);

  // Individual sub-hashes.
  const [
    canvasHash, webglImageHash, webglParamsHash, webgl2ParamsHash,
    audioHash, fontHash, pluginHash, mimeTypesHash, mathHash, systemColorsHash,
  ] = await Promise.all([
    canvas ? sha256Short(canvas) : Promise.resolve(null),
    wgl1.imageHash ? sha256Short(wgl1.imageHash) : Promise.resolve(null),
    wgl1.paramsHash ? sha256Short(wgl1.paramsHash) : Promise.resolve(null),
    wgl2.paramsHash ? sha256Short(wgl2.paramsHash) : Promise.resolve(null),
    audio ? sha256Short(audio) : Promise.resolve(null),
    fonts && fonts.length ? sha256Short(fonts.join(",")) : Promise.resolve(null),
    pluginList && pluginList.length ? sha256Short(pluginList.join(",")) : Promise.resolve(null),
    mimeSeed ? sha256Short(mimeSeed) : Promise.resolve(null),
    mathS ? sha256Short(mathS) : Promise.resolve(null),
    sysColorsSeed ? sha256Short(sysColorsSeed) : Promise.resolve(null),
  ]);

  // Stable composite: hardware + render layers (survives most browser changes).
  const deviceSeed = [
    platform, vendor, deviceMemory, concurrency, touch,
    screen_.width, screen_.height, screen_.color_depth, screen_.pixel_ratio,
    canvasHash, webglImageHash, webglParamsHash, webgl2ParamsHash, audioHash, fontHash,
  ].join("~");
  const deviceHash = await sha256Short(deviceSeed);

  // Browser-volatile composite (UA-ish layer).
  const browserSeed = [
    tryCatch(() => nav.userAgent, ""), languages?.join(",") ?? "",
    timezone, tzOffset, platform, vendor,
  ].join("~");
  const browserHash = await sha256Short(browserSeed);

  // Cookie/storage-bound hash (combines persistent id with the device layer).
  const cookieHash = await sha256Short(`${trueDeviceId}~${deviceHash}`);

  return {
    device_hash: deviceHash,
    true_device_id: trueDeviceId,
    browser_hash: browserHash,
    cookie_hash: cookieHash,
    canvas_hash: canvasHash,
    webgl_hash: webglParamsHash,
    webgl2_hash: webgl2ParamsHash,
    webgl_image_hash: webglImageHash,
    webgl_params_hash: webglParamsHash,
    audio_hash: audioHash,
    font_list_hash: fontHash,
    spoofing_hash: null, // filled by tamper.ts merge
    screen: screen_,
    touch_support: touch,
    device_memory: deviceMemory,
    hardware_concurrency: concurrency,
    platform,
    vendor,
    timezone,
    timezone_offset: tzOffset,
    languages,
    do_not_track: dnt,
    gpc,
    // enriched safe-subset signals
    font_list: fonts,
    plugin_list: pluginList,
    plugin_count: pluginList ? pluginList.length : null,
    plugin_hash: pluginHash,
    mime_types_hash: mimeTypesHash,
    math_hash: mathHash,
    system_colors_hash: systemColorsHash,
    webgl_vendor: wgl1.vendor,
    webgl_renderer: wgl1.renderer,
    locale,
    timezone_country: null,
    user_agent_data: userAgentData,
    media_devices: mediaDevices,
    window_location: windowLocation,
    window_hostname: windowHostname,
    // non-typed extras consumed by the orchestrator before sealing
    ...( { __persistent_id_present: present_before } as Record<string, unknown> ),
  } as DeviceSignals;
}
